/**
 * "Tra cứu" next to a tax-code (MST) field (owner decision 2026-09-30, OT&E finding F16): looks
 * the code up in the business registry and shows what it holds; staff confirm before anything is
 * filled. Only the registered name (and trade name, founding date) are filled; the legal
 * representative's name is shown to compare with what staff type (names are never split), and
 * the registry's address (an older administrative list) is shown, not filled. An enterprise the
 * registry does not show as in operation is not used.
 */

import { Search, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { api, isApiError } from '../api';
import { useAccess } from '../lib/access';
import { registryFacts, sameFullName, type RegistryFacts } from '../lib/businessRegistry';

export function TaxCodeLookup({
  code,
  household = false,
  representative,
  onUse,
}: {
  /** The tax code as typed. */
  code: string;
  /** A household business (looked up by its tax code in the household registry). */
  household?: boolean;
  /** The representative's names as typed, to compare with the registered one. */
  representative?: { last: string; first: string };
  onUse: (facts: RegistryFacts) => void;
}) {
  const { t } = useTranslation('accounts');
  const access = useAccess();
  const [state, setState] = useState<{ phase: 'idle' | 'busy' } | { phase: 'done'; facts: RegistryFacts } | { phase: 'error'; message: string }>({ phase: 'idle' });
  if (!access.has(['business_registry.lookup'])) return null;
  const value = code.replace(/\s+/g, '');
  const run = async () => {
    setState({ phase: 'busy' });
    try {
      const r = household ? await api.lookupVnnicHousehold(value) : await api.lookupVnnicEnterprise(value);
      setState({ phase: 'done', facts: registryFacts(r, household) });
    } catch (e) {
      const errCode = isApiError(e) ? e.code : '';
      const message =
        errCode === 'VNNIC_REGISTRAR_API_DISABLED'
          ? t('taxLookup.notConnected')
          : errCode === 'FORBIDDEN' || errCode === 'PERMISSION_DENIED'
            ? t('taxLookup.forbidden')
            : errCode === 'VALIDATION_FAILED'
              ? t('taxLookup.badCode')
              : t('taxLookup.failed');
      setState({ phase: 'error', message });
    }
  };
  const facts = state.phase === 'done' ? state.facts : null;
  const match = facts && representative ? sameFullName(representative.last, representative.first, facts.representative) : null;
  return (
    <div className="grid gap-2 sm:col-span-2" data-tax-lookup>
      <div>
        <Button type="button" size="sm" variant="outline" onClick={() => void run()} disabled={!value || state.phase === 'busy'}>
          <Search /> {state.phase === 'busy' ? t('taxLookup.running') : t('taxLookup.button')}
        </Button>
      </div>
      {state.phase === 'error' ? <p className="text-sm text-critical">{state.message}</p> : null}
      {facts && !facts.found ? <p className="text-sm text-muted-foreground">{t('taxLookup.notFound')}</p> : null}
      {facts && facts.found ? (
        <div className="grid gap-2 rounded-md border bg-muted/30 p-3 text-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="font-medium">{t('taxLookup.title')}</div>
            <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setState({ phase: 'idle' })} aria-label={t('taxLookup.close')}>
              <X className="size-4" />
            </button>
          </div>
          <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[max-content_1fr]">
            <dt className="text-muted-foreground">{t('taxLookup.name')}</dt>
            <dd>{facts.name ?? '—'}</dd>
            {facts.shortName ? (
              <>
                <dt className="text-muted-foreground">{t('taxLookup.shortName')}</dt>
                <dd>{facts.shortName}</dd>
              </>
            ) : null}
            {facts.status ? (
              <>
                <dt className="text-muted-foreground">{t('taxLookup.status')}</dt>
                <dd className={facts.active === false ? 'text-critical' : undefined}>{facts.status}</dd>
              </>
            ) : null}
            {facts.foundingDate ? (
              <>
                <dt className="text-muted-foreground">{household ? t('taxLookup.registeredOn') : t('taxLookup.foundedOn')}</dt>
                <dd>{facts.foundingDate}</dd>
              </>
            ) : null}
            {facts.representative ? (
              <>
                <dt className="text-muted-foreground">{household ? t('taxLookup.owner') : t('taxLookup.representative')}</dt>
                <dd>
                  {facts.representative}
                  {match === true ? <span className="ml-2 text-success">{t('taxLookup.nameMatches')}</span> : null}
                  {match === false ? <span className="ml-2 text-warning">{t('taxLookup.nameDiffers')}</span> : null}
                </dd>
              </>
            ) : null}
            {facts.address ? (
              <>
                <dt className="text-muted-foreground">{t('taxLookup.address')}</dt>
                <dd>
                  {facts.address} <span className="text-xs text-muted-foreground">({t('taxLookup.addressNote')})</span>
                </dd>
              </>
            ) : null}
          </dl>
          {facts.active === false ? <p className="text-critical">{t('taxLookup.notActive')}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={facts.active === false || !facts.name}
              onClick={() => {
                onUse(facts);
                setState({ phase: 'idle' });
              }}
            >
              {t('taxLookup.use')}
            </Button>
            <span className="self-center text-xs text-muted-foreground">{t('taxLookup.useHint')}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
